import React from "react"
import { X } from "react-feather"
import style from "./Notification.module.scss"

let id = 0
const delay = 5000

type Notify = (notification: React.ReactNode, duration?: number) => void

const context = React.createContext<{
  notify: Notify
}>({
  notify: notification => notification
})
type Message = {
  key: number
  msg: React.ReactNode
}

export const useNotification = () => {
  const { notify } = React.useContext(context)
  return notify
}

const { Provider } = context

const Message: React.FunctionComponent<{
  item: Message
  pause: boolean
  duration: number
  setItems: React.Dispatch<React.SetStateAction<Message[]>>
  timer: [number, React.Dispatch<React.SetStateAction<number>>]
}> = ({ item, setItems, timer, pause, duration }) => {
  const closeDelay = 500
  const [time, setTime] = timer
  const [isClosing, setClosing] = React.useState<boolean>()
  const isMounted = React.useRef(true)
  const message = React.useRef<HTMLDivElement>(null)
  const progress = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isMounted) return
    setTime(Date.now())
    setClosing(false)
    return () => {
      isMounted.current = false
    }
  }, [setTime])

  React.useEffect(() => {
    if (!message.current || pause) return
    const timer = setTimeout(() => {
      if (!isMounted) return
      setClosing(true)
    }, duration)
    return () => clearTimeout(timer)
  })

  React.useEffect(() => {
    if (!isClosing) return
    const timer = setTimeout(() => {
      if (!isMounted) return
      setItems(items => items.filter(({ key }) => key !== item.key))
    }, closeDelay)
    return () => clearTimeout(timer)
  }, [isClosing, setItems, item.key])

  const { progressAnimation, progressStyle } = React.useMemo(() => {
    const progressAnimation = `
      @keyframes react-message-progress-animation-${item.key}-${time} {
        0%   { width: ${progress.current?.offsetWidth}px; }
        100% { width: 100%; }
      }
    `
    const progressStyle: React.CSSProperties = {
      animationName: `react-message-progress-animation-${item.key}-${time}`,
      width: progress.current?.offsetWidth
    }
    if (typeof isClosing === "undefined" || pause) {
      progressStyle.animationPlayState = "paused"
    } else {
      progressStyle.animationDuration = `${duration + closeDelay}ms`
      progressStyle.animationPlayState = "running"
    }
    return { progressStyle, progressAnimation }
  }, [isClosing, pause, duration, item.key, time])

  const offsetHeight = message.current?.offsetHeight || 0

  const { enterAnimation, enterStyle } = React.useMemo(() => {
    const animationName = `react-message-enter-animation-${item.key}`
    const enterAnimation = `
      @keyframes ${animationName} {
        0%   { height: 0px; opacity:0;}
        100% { height: calc(${offsetHeight}px + 1rem); opacity: 1;}
      }
    `
    const enterStyle: React.CSSProperties = offsetHeight
      ? {
          animationName: animationName,
          height: `calc(${offsetHeight}px + 1rem)`
        }
      : {}
    return { enterAnimation, enterStyle }
  }, [offsetHeight, item.key])

  return (
    <aside className={`${style.messageCont} ${isClosing ? style.isClosing : ""}`} style={enterStyle}>
      <style>{enterAnimation}</style>
      <div className={style.message} ref={message}>
        <div className={style.messageContent}>
          <div
            role="button"
            className={style.close}
            onClick={e => {
              e.stopPropagation()
              if (!isMounted) return
              setClosing(true)
            }}
          >
            <X size={18} aria-label="Chiudi" />
          </div>
          <p className={style.content}>{item.msg}</p>
        </div>
        <style>{progressAnimation}</style>
        <div ref={progress} className={style.progress} style={progressStyle} />
      </div>
    </aside>
  )
}

const MessageHub: React.FunctionComponent<{ render: (msg: Notify) => void }> = ({ render }) => {
  const [items, setItems] = React.useState<Message[]>([])
  const [time, setTime] = React.useState<number>(Date.now())
  const [duration, setDuration] = React.useState<number>(delay)
  const [pause, setPause] = React.useState<boolean>(false)

  React.useEffect(
    () =>
      render((msg: React.ReactNode) => {
        setItems(state => [...state, { key: id++, msg }])
        setDuration(delay)
      }),
    [render]
  )

  return (
    <div
      className={style.container}
      onMouseEnter={() => {
        setDuration(prev => prev - (Date.now() - time))
        setTime(Date.now())
        setPause(true)
      }}
      onMouseLeave={() => {
        setTime(Date.now())
        setPause(false)
      }}
    >
      {items &&
        items
          .map(item => (
            <Message
              timer={[time, setTime]}
              duration={duration}
              pause={pause}
              key={item.key}
              item={item}
              setItems={setItems}
            />
          ))
          .reverse()}
    </div>
  )
}

const NotificationHub = ({ children }: { children: React.ReactNode }) => {
  // ref instead of state to avoid rerenders
  // https://www.codebeast.dev/usestate-vs-useref-re-render-or-not/
  // https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  const ref = React.useRef<Notify>(notification => notification)
  const notify: Notify = React.useCallback((notification, delay = 250) => {
    setTimeout(() => {
      ref.current(notification)
    }, delay)
  }, [])
  const contextValue = React.useMemo(() => ({ notify }), [notify])
  const attach = React.useCallback((add: Notify) => {
    ref.current = add
  }, [])
  return (
    <Provider value={contextValue}>
      {children}
      <MessageHub render={attach} />
    </Provider>
  )
}

const memoized = React.memo(NotificationHub)
NotificationHub.displayName = "NotificationHub.memo"

export { memoized as NotificationHub }
